import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Show from 'components/Form/Show';
import BranchCollection from 'components/Form/BranchCollection';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { PsychologicalOrderTypeModel } from 'forms/sections/psychological/shared/OrderType';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import AppealItem from './AppealItem';

interface Props extends Partial<PsychologicalOrderTypeModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  required?: boolean
  prefix?: string
  attribute?: string
  formType?: string
}

export default class Order extends ValidationElement<Props> {
  update = (queue: Partial<PsychologicalOrderTypeModel>) => {
    this.props.onUpdate({
      Occurred: this.props.Occurred,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Disposition: this.props.Disposition,
      Appeals: this.props.Appeals,
      Comments: this.props.Comments,
      AppealsComments: this.props.AppealsComments,
      AppealsSummaryComments: this.props.AppealsSummaryComments,
      ...queue,
    });
  };

  updateOccurred = (values: PsychologicalOrderTypeModel['Occurred']) => {
    this.update({
      Occurred: values,
    });
  };

  updateCourtName = (values: PsychologicalOrderTypeModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtAddress = (values: PsychologicalOrderTypeModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateDisposition = (values: PsychologicalOrderTypeModel['Disposition']) => {
    this.update({
      Disposition: values,
    });
  };

  updateAppeals = (values: PsychologicalOrderTypeModel['Appeals']) => {
    this.update({
      Appeals: values,
    });
  };

  updateComments = (values: PsychologicalOrderTypeModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateAppealsComments = (values: PsychologicalOrderTypeModel['AppealsComments']) => {
    this.update({
      AppealsComments: values,
    });
  };

  updateAppealsSummaryComments = (values: PsychologicalOrderTypeModel['AppealsSummaryComments']) => {
    this.update({
      AppealsSummaryComments: values,
    });
  };

  branchComment = () => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <Comments
        title={i18n.t(`psychological.appealsComments`)}
        name="AppealsComments"
        attribute={`${attribute}.AppealsComment`}
        className="optional-comments"
        {...this.props.AppealsComments}
        onUpdate={this.updateAppealsComments}
        onError={this.props.onError}
        required={false}
      />
    );
  };

  render() {
    const prefix = this.props.prefix;
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="order">
        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.courtName`)}
          attribute={`${attribute}.CourtName`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="CourtName"
            attribute={`${attribute}.CourtName`}
            className="courtname"
            {...this.props.CourtName}
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.occurred`)}
          attribute={`${attribute}.Occurred`}
          help={`psychological.${prefix}.help.occurred`}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Occurred"
            attribute={`${attribute}.Occurred`}
            {...this.props.Occurred}
            maxDate={{ year: new Date().getFullYear() }}
            minDateEqualTo
            onUpdate={this.updateOccurred}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.CourtAddress`}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          dataTestId="orderAddress"
        >
          <PVQLocation
            name="CourtAddress"
            attribute={`${attribute}.CourtAddress`}
            {...this.props.CourtAddress}
            label={i18n.t(`psychological.pvq.${prefix}.label.courtAddress`)}
            domesticTitle={i18n.t(`psychological.pvq.${prefix}.label.courtAddressDomesticTitle`)}
            foreignTitle={i18n.t(`psychological.pvq.${prefix}.label.courtAddressForeignTitle`)}
            includeMilitaryStates
            layout={Location.ADDRESS}
            geocode
            addressBook="Court"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showMilitaryInstallation={false}
            showInternationalStreet={false}
          />
        </Field>
        <Show when={prefix !== 'competence'}>
          <Field
            title={i18n.t(`psychological.pvq.${prefix}.label.disposition`)}
            attribute={`${attribute}.Disposition`}
            help="psychological.pvq.consultation.help.disposition"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              name="Disposition"
              attribute={`${attribute}.Disposition`}
              className="disposition"
              {...this.props.Disposition}
              onUpdate={this.updateDisposition}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>
        <BranchCollection
          className="appeals"
          {...this.props.Appeals}
          attribute={`${attribute}.Appeals`}
          label={i18n.t(`psychological.pvq.${prefix}.heading.appealed`)}
          appendLabel={i18n.t(`psychological.pvq.${prefix}.heading.appealedAnother`)}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateAppeals}
          scrollIntoView={this.props.scrollIntoView}
        >
          <AppealItem
            name="Item"
            attribute={`${attribute}.Appeals`}
            bind
            prefix={this.props.prefix}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
          />
        </BranchCollection>

      </div>

    ) : (
      <div className="order">
        <Field
          title={i18n.t(`psychological.${prefix}.heading.occurred`)}
          attribute={`${attribute}.Occurred`}
          help={`psychological.${prefix}.help.occurred`}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Occurred"
            attribute={`${attribute}.Occurred`}
            {...this.props.Occurred}
            hideDay
            maxDate={{ year: new Date().getFullYear() }}
            minDateEqualTo
            onUpdate={this.updateOccurred}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.courtName`)}
          attribute={`${attribute}.CourtName`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="CourtName"
            attribute={`${attribute}.CourtName`}
            className="courtname"
            {...this.props.CourtName}
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.courtAddress`)}
          attribute={`${attribute}.CourtAddress`}
          optional
          help={`psychological.${prefix}.help.courtAddress`}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="CourtAddress"
            attribute={`${attribute}.CourtAddress`}
            {...this.props.CourtAddress}
            label={i18n.t(`psychological.${prefix}.label.courtAddress`)}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
          />
        </Field>
        <Comments
          title={i18n.t(`psychological.${prefix}.comments`)}
          name="Comments"
          attribute={`${attribute}.Comments`}
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
        <Show when={prefix !== 'competence'}>
          <Field
            title={i18n.t(`psychological.${prefix}.heading.disposition`)}
            attribute={`${attribute}.Disposition`}
            help="psychological.consultation.help.disposition"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              name="Disposition"
              attribute={`${attribute}.Disposition`}
              className="disposition"
              {...this.props.Disposition}
              onUpdate={this.updateDisposition}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <BranchCollection
          className="appeals"
          {...this.props.Appeals}
          attribute={`${attribute}.Appeals`}
          label={i18n.t(`psychological.${prefix}.heading.appealed`)}
          appendLabel={i18n.t(`psychological.${prefix}.heading.appealedAnother`)}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateAppeals}
          scrollIntoView={this.props.scrollIntoView}
          branchComment={this.branchComment}
        >
          <AppealItem
            name="Item"
            attribute={`${attribute}.Appeals`}
            bind
            prefix={this.props.prefix}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </BranchCollection>
        {this.props.Appeals?.items?.[0]?.Item?.Has?.value === 'Yes' && (
          <Comments
            title={i18n.t(`psychological.${prefix}.summaryComments`)}
            name="SummaryComments"
            attribute={`${attribute}.AppealsSummaryComments`}
            className="optional-comments"
            {...this.props.AppealsSummaryComments}
            onUpdate={this.updateAppealsSummaryComments}
            onError={this.props.onError}
            required={false}
            negativeMarginTop={false}
          />
        )}
      </div>
    );
  }
}

Order.defaultProps = {
  attribute: 'List',
  List: [],
  Comments: {},
  AppealsComment: {},
  AppealsSummaryComments: {},
  prefix: 'order',
  addressBooks: {},
  formType: '',
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
